<template>
  <BaseFormLayout
    :title="`${editMode ? 'Edit' : 'Add'} User`"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <SecondaryButton
        id="userReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
        Reset
      </SecondaryButton>
      <PrimaryButton
        id="userAdd"
        @click="submitForm()"
        :disabled="!isValid"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="normalUserForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row no-gutters>
          <v-col cols="12">
            <v-card elevation="0" class="px-4 pt-4 pb-12 rounded-lg">
              <v-row no-gutters>
                <v-col cols="12" class="pa-4">
                  <span class="text-subtitle-1 font-weight-bold">
                    Basic Information
                  </span>
                </v-col>
                <v-col cols="12" lg="8">
                  <v-row class="ma-0">
                    <v-col cols="12" lg="4">
                      <SelectField
                        id="branches"
                        v-model="normalUserDetails.branches"
                        :items-list="branchList"
                        name="branches"
                        item-text="name"
                        item-value="id"
                        :label="`${configurationModule.branchPlural}*`"
                        :multiple="true"
                        :rules="[
                          (v) =>
                            !!v.length > 0 ||
                            `${configurationModule.branchPlural} are required`,
                        ]"
                        :error-messages="formErrors.branches"
                        @input="delete formErrors.branches"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="username"
                        v-model="normalUserDetails.username"
                        label="Username"
                        name="username"
                        :required="true"
                        :rules="[
                          (val) =>
                            (val && usernameValidation(val)) ||
                            'No special characters are allowed',
                        ]"
                        :error-messages="formErrors.username"
                        @input="delete formErrors.username"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="first_name"
                        v-model="normalUserDetails.first_name"
                        label="First Name"
                        name="first_name"
                        :required="true"
                        :error-messages="formErrors.first_name"
                        @input="delete formErrors.first_name"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="last_name"
                        v-model="normalUserDetails.last_name"
                        label="Last Name"
                        name="last_name"
                        :required="true"
                        :error-messages="formErrors.last_name"
                        @input="delete formErrors.last_name"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="contact_number"
                        type="number"
                        name="contact_number"
                        v-model="normalUserDetails.contact_number"
                        label="Contact Number"
                        :required="true"
                        :rules="[
                          (val) =>
                            (val && val.length >= 9 && val.length <= 12) ||
                            'Contact number should be between 9 to 12 digits!',
                        ]"
                        :error-messages="formErrors.contact_number"
                        @input="delete formErrors.contact_number"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <SelectField
                        id="preferred_timezone"
                        v-model="normalUserDetails.preferred_timezone"
                        name="preferred_timezone"
                        :itemsList="timezoneList"
                        itemText="name"
                        itemValue="id"
                        :rules="[(val) => !!val || 'Timezone is required']"
                        label="Timezone *"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <PasswordField
                        id="password"
                        label="Password"
                        name="password"
                        v-model="normalUserDetails.password"
                        :required="!editMode"
                        :error-messages="formErrors.password"
                        @input="delete formErrors.password"
                        :rules="
                          normalUserDetails.password != null &&
                          normalUserDetails.confirm_password != null
                            ? passwordValidation
                            : []
                        "
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <PasswordField
                        id="confirm_password"
                        label="Confirm password"
                        name="confirm_password"
                        v-model="normalUserDetails.confirm_password"
                        :required="!editMode || !!normalUserDetails.password"
                        :rules="
                          normalUserDetails.password != null &&
                          normalUserDetails.confirm_password != null
                            ? passwordValidation
                            : []
                        "
                        :error-messages="formErrors.confirm_password"
                        @input="delete formErrors.confirm_password"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="email"
                        v-model="normalUserDetails.email"
                        label="Email"
                        name="email"
                        :required="true"
                        :rules="
                          normalUserDetails.email
                            ? [
                                (v) =>
                                  (v && emailValidation(v)) ||
                                  'Invalid email id',
                              ]
                            : []
                        "
                        :error-messages="formErrors.email"
                        @input="delete formErrors.email"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <SelectField
                        id="role"
                        v-model="normalUserDetails.role"
                        :items-list="roleList"
                        name="role"
                        item-text="name"
                        item-value="id"
                        :label="`${configurationModule.role}`"
                        :required="false"
                        :error-messages="formErrors.role"
                        @input="delete formErrors.role"
                        @change="getRoleDetails()"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" lg="4" class="d-flex justify-center">
                  <ProfileUpload
                    v-model="profileImage"
                    @UploadProfile="updateProfile"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" class="mt-4">
            <v-card class="card-box-shadow px-4 rounded-lg" elevation="0">
              <v-row no-gutters>
                <v-col
                  cols="12"
                  class="pt-6 pb-3 d-flex justify-space-between ma-0"
                >
                  <span
                    class="font-weight-bold text-subtitle-1 text-capitalize"
                  >
                    permissions
                  </span>
                  <SecondaryButton
                    btnClass="text-uppercase"
                    @click="selectAllPermission"
                    :name="isAllSelected ? 'Unselect all' : 'Select All'"
                  >
                  </SecondaryButton>
                </v-col>

                <v-col cols="12" class="pt-6 pb-12">
                  <v-row>
                    <v-col cols="12" class="subHeading lighten-1 px-10">
                      <v-row>
                        <v-col class="d-flex algin-center">
                          <span class="secondary--text text-capitalize">
                            module
                          </span>
                        </v-col>
                        <v-col class="d-flex algin-center">
                          <v-checkbox
                            color="success"
                            class="my-0 mr-2 ml-0 view-checkbox"
                            :value="isAllViewSelected"
                            @click="toggleAllViewPermission"
                            hide-details
                          >
                          </v-checkbox>
                          <span class="secondary--text text-capitalize pt-1">
                            view
                          </span>
                        </v-col>
                        <v-col class="d-flex algin-center">
                          <v-checkbox
                            color="success"
                            class="my-0 mr-2 ml-0 view-checkbox"
                            :value="isAllAddSelected"
                            @click="toggleAllAddPermission"
                            hide-details
                          >
                          </v-checkbox>
                          <span class="secondary--text text-capitalize pt-1">
                            add
                          </span>
                        </v-col>
                        <v-col class="d-flex algin-center">
                          <v-checkbox
                            color="success"
                            class="my-0 mr-2 ml-0 view-checkbox"
                            :value="isAllChangeSelected"
                            @click="toggleAllChangePermmission"
                            hide-details
                          >
                          </v-checkbox>
                          <span class="secondary--text text-capitalize pt-1">
                            change
                          </span>
                        </v-col>
                        <v-col class="d-flex algin-center">
                          <v-checkbox
                            color="success"
                            class="my-0 mr-2 ml-0 view-checkbox"
                            :value="isAllDeleteSelected"
                            @click="toggleAllDeletePermmission"
                            hide-details
                          >
                          </v-checkbox>
                          <span class="secondary--text text-capitalize pt-1">
                            delete
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col
                      class="px-10"
                      cols="12"
                      v-for="(permission, key) in userPermissions"
                      :key="key"
                    >
                      <v-row>
                        <v-col class="text-capitalize">
                          <span>
                            {{ key.replace(/_/g, " ") }}
                          </span>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            color="success"
                            class="mt-1"
                            :name="`${key}-view`"
                            v-model="permission.view"
                            hide-details
                            @change="valueChanged(key, permission.view, 'view')"
                          >
                          </v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            color="success"
                            class="mt-1"
                            :name="`${key}-add`"
                            v-model="permission.add"
                            hide-details
                            @change="valueChanged(key, permission.add, 'add')"
                          >
                          </v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            color="success"
                            class="mt-1"
                            :name="`${key}-change`"
                            v-model="permission.change"
                            hide-details
                            @change="
                              valueChanged(key, permission.change, 'change')
                            "
                          >
                          </v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            color="success"
                            class="mt-1"
                            hide-details
                            :name="`${key}-delete`"
                            v-model="permission.delete"
                            @change="
                              valueChanged(key, permission.delete, 'delete')
                            "
                          >
                          </v-checkbox>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import {
  generatedFormData,
  emailValidation,
  usernameValidation,
} from "@/utils/functions.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import { configurationModule } from "@/utils/moduleMapping";
import { timezones } from "@/utils/fieldConstants.js";
export default {
  components: {
    BaseFormLayout,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
  },
  data() {
    return {
      timezoneList: timezones,
      emailValidation,
      usernameValidation,
      configurationModule,
      viewPassword: false,
      oldPassword: false,
      fileInput: null,
      formCount: 0,
      isValid: true,
      normalUserDetails: {},
      formErrors: {},
      profileImage: null,

      branchList: [],
      roleList: [],
      userPermissions: {
        user: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        trip_planning: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        trip: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        report: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        order: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        operation: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        integration: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        fleet: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        customer: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
        configuration: {
          delete: false,
          change: false,
          add: false,
          view: false,
        },
      },
    };
  },
  computed: {
    passwordValidation() {
      return [
        this.normalUserDetails.password ==
          this.normalUserDetails.confirm_password || `Password does not match`,
      ];
    },
    isAllViewSelected() {
      return Object.values(this.userPermissions).every((obje) => obje.view);
    },
    isAllAddSelected() {
      return Object.values(this.userPermissions).every((obje) => obje.add);
    },
    isAllChangeSelected() {
      return Object.values(this.userPermissions).every((obje) => obje.change);
    },
    isAllDeleteSelected() {
      return Object.values(this.userPermissions).every((obje) => obje.delete);
    },
    isAllSelected() {
      return (
        this.isAllAddSelected &&
        this.isAllChangeSelected &&
        this.isAllDeleteSelected &&
        this.isAllViewSelected
      );
    },
    anyPermissionSelected() {
      return Object.values(this.userPermissions).every((obje) => {
        return Object.values(obje).every((val) => val == false);
      });
    },
    userDetails() {
      let obj = this.$helper.getUserDetailsLocalStorage();
      if (obj) {
        return obj;
      }
    },
  },
  methods: {
    resetForm() {
      this.$refs.normalUserForm.reset();
    },
    getBranchList() {
      bus.$emit("showLoader", true);
      this.$api.branch
        .getBranchSelectList({ limit: "all" })
        .then((res) => {
          this.branchList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getRoleList() {
      bus.$emit("showLoader", true);
      this.$api.role
        .getRoleList({ limit: "all" })
        .then((res) => {
          this.roleList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    toggleAllViewPermission() {
      let obj = { ...this.userPermissions };
      if (this.isAllViewSelected) {
        Object.values(obj).map((module) => {
          module.view = false;
        });
      } else {
        Object.values(obj).map((module) => {
          module.view = true;
        });
      }
      this.userPermissions = obj;
    },
    toggleAllAddPermission() {
      let obj = { ...this.userPermissions };
      if (this.isAllAddSelected) {
        Object.values(obj).map((module) => {
          module.add = false;
        });
      } else {
        Object.values(obj).map((module) => {
          module.add = true;
          module.view = true;
        });
      }
      this.userPermissions = obj;
    },
    toggleAllChangePermmission() {
      let obj = { ...this.userPermissions };
      if (this.isAllChangeSelected) {
        Object.values(obj).map((module) => {
          module.change = false;
        });
      } else {
        Object.values(obj).map((module) => {
          module.change = true;
          module.view = true;
          module.add = true;
        });
      }
      this.userPermissions = obj;
    },
    toggleAllDeletePermmission() {
      let obj = { ...this.userPermissions };
      if (this.isAllDeleteSelected) {
        Object.values(obj).map((module) => {
          module.delete = false;
        });
      } else {
        Object.values(obj).map((module) => {
          module.delete = true;
          module.add = true;
          module.view = true;
          module.change = true;
        });
      }
      this.userPermissions = obj;
    },
    selectAllPermission() {
      let obj = { ...this.userPermissions };
      if (this.isAllSelected) {
        Object.values(obj).map((module) => {
          module.add = false;
          module.view = false;
          module.delete = false;
          module.change = false;
        });
      } else {
        Object.values(obj).map((module) => {
          module.add = true;
          module.view = true;
          module.delete = true;
          module.change = true;
        });
      }
      this.userPermissions = obj;
    },
    valueChanged(obj, input, type) {
      let moduleName = obj;
      let permission = input;
      let setAllTrue = {
        add: true,
        change: true,
        delete: true,
        view: true,
      };
      let setAllFalse = {
        add: false,
        change: false,
        delete: false,
        view: false,
      };
      if (permission && type == "delete") {
        this.userPermissions[moduleName] = setAllTrue;
      } else if (!permission && type == "view") {
        this.userPermissions[moduleName] = setAllFalse;
      } else if (permission && type == "change") {
        this.userPermissions[moduleName].add = true;
        this.userPermissions[moduleName].view = true;
        this.userPermissions[moduleName].change = true;
      } else if (!permission && type == !"view") {
        this.userPermissions[moduleName].type = false;
      }
      this.$forceUpdate();
    },
    getRoleDetails() {
      if (this.normalUserDetails && this.normalUserDetails.role) {
        bus.$emit("showLoader", true);
        this.$api.role
          .getRoleObject(this.normalUserDetails.role)
          .then((res) => {
            this.userPermissions = res.data.permissions;
            if (!this.userDetails.company_details.configuration.has_items) {
              delete this.userPermissions.item;
            }
            bus.$emit("showLoader", false);
          })
          .catch((err) => {
            console.log(err);
            bus.$emit("showLoader", false);
          });
      }
    },
    getNormalUserObject(id) {
      bus.$emit("showLoader", true);
      this.$api.normalUser
        .getNormalUserObject(id)
        .then((res) => {
          if (res?.data?.profile_image) {
            this.profileImage = res.data.profile_image;
            delete res.data.profile_image;
          }
          this.normalUserDetails = res.data;
          if (res && res.data && res.data.user_permissions) {
            this.userPermissions = res.data.user_permissions;
          }
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    updateProfile(image, isChangeable = false) {
      if (this.normalUserDetails.update_profile_image || isChangeable) {
        this.normalUserDetails.update_profile_image = true;
        if (image) {
          this.normalUserDetails.profile_image = image;
        } else {
          delete this.normalUserDetails.profile_image;
        }
      } else {
        this.normalUserDetails.update_profile_image = false;
        delete this.normalUserDetails.profile_image;
      }
    },
    submitForm() {
      if (!this.$refs.normalUserForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }

      if (this.anyPermissionSelected) {
        bus.$emit("showToastMessage", {
          message: "Please select permission before submitting",
          color: "error",
        });
        return;
      }
      if (this.userPermissions) {
        this.normalUserDetails.user_permissions = JSON.stringify({
          ...this.userPermissions,
        });
      }
      if (this.editMode) {
        if (
          this.normalUserDetails &&
          !this.normalUserDetails.update_profile_image
        ) {
          delete this.normalUserDetails.profile_image;
        }

        this.$api.normalUser
          .updateNormalUserObject({
            id: this.normalUserDetails.id,
            data: generatedFormData(this.normalUserDetails),
          })
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "User information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            console.log(err);
            this.formErrors = err.data;
          });
      } else {
        this.$api.normalUser
          .createNormalUserObject(generatedFormData(this.normalUserDetails))
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "User created!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            this.formErrors = err.data;
          });
      }
    },
  },
  mounted() {
    if (this.userDetails.company_details.configuration.has_items) {
      this.userPermissions.item = {
        delete: false,
        change: false,
        add: false,
        view: false,
      };
    }
    this.getBranchList();
    this.getRoleList();
    if ("id" in this.$route.params) {
      this.getNormalUserObject(this.$route.params.id);
    }
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
